import {
  Button,
  CustomPageContainer,
  ProcessesIcon,
  TabItemProps,
  TablePaginationContextProvider,
} from '@/components';
import { BreadcrumbItem } from '@/components/Breadcrumbs';
import { LabelIcon } from '@/components/LabelIcon/LabelIcon';
import { PageDetailsActions } from '@/components/PageDetailsActions/PageDetailsActions';
import { TabsWrapper } from '@/components/TabsWrapper';
import { queryClient } from '@/constants';
import { RoutePath } from '@/core/router';
import { t } from 'i18next';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ProcessKey,
  useDisableProcess,
  useEnableProcess,
  useGetProcess,
} from '../../queries';
import {
  AssociatedAutomationsTab,
  ProcessHeaderRow,
  ProcessPerformanceCards,
} from './components';
import { ProcesssLastRunSuccessRateCard } from './components/ProcessPerformanceCharts/ProcessLastRunSuccessRateCard';
import { ProcessOverallSuccessRateCard } from './components/ProcessPerformanceCharts/ProcessOverallSuccessRateCard';
import { ProcessTimeDistributionCard } from './components/ProcessPerformanceCharts/ProcessTimeDistributionCard';
import { ProcessWorkflowModal } from './components/ProcessWorkflowModal';
import { WorkflowsTable } from './components/WorkflowsTables/WorkflowsTables';

export const ProcessDetailPage = (): ReactElement => {
  const { processId } = useParams<{ processId: string }>();
  const parsedProcessId = +processId!;

  const [status, setStatus] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const navigate = useNavigate();
  const { mutate: enableProcess } = useEnableProcess();
  const { mutate: disableProcess } = useDisableProcess();

  const {
    data: process,
    isLoading: isLoadingProcess,
    isError,
    error,
  } = useGetProcess(parsedProcessId);

  const handleModalState = useCallback(() => {
    setIsOpen((prevStateOpen) => !prevStateOpen);
  }, []);

  const handleStatus = useCallback(
    (checked: boolean) => {
      if (!process?.id) {
        return toast.error(t('generic_errors'));
      }
      setStatus(checked);
      if (checked) {
        enableProcess(process?.id, {
          onError: () => {
            toast.error(t('error_enable_process'));
          },
          onSuccess: () => {
            toast.success(t('success_enable_process'));
            queryClient.invalidateQueries(ProcessKey.ALL);
          },
        });
      } else {
        disableProcess(process?.id, {
          onError: () => {
            toast.error(t('error_disable_process'));
          },
          onSuccess: () => {
            toast.success(t('success_disable_process'));
            queryClient.invalidateQueries(ProcessKey.ALL);
          },
        });
      }
    },
    [process, enableProcess, disableProcess],
  );

  const routeChange = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const sortedWorkflows = useMemo(() => {
    return (
      process?.workflows.sort((w1, w2) => (w1.order > w2.order ? 1 : -1)) ?? []
    );
  }, [process?.workflows]);

  useEffect(() => {
    if (isError && error) {
      toast.error(t('error_get_process_details'));
    }
  }, [isError, error, navigate]);

  useEffect(() => {
    setStatus(process?.status === true);
  }, [process]);

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    if (!process) return [];
    return [
      {
        label: process.organization,
        href: RoutePath.organizationDetail(process.organizationId.toString()),
      },
      {
        label: process.company,
        href: RoutePath.companyDetail(process.companyId.toString()),
      },
      {
        label: process.businessAreaName,
        href: RoutePath.businessAreaDetail(process.businessAreaId.toString()),
      },
      {
        label: t('processes'),
        href: RoutePath.processes(),
      },
      {
        label: process.name,
      },
    ];
  }, [process]);

  const tabs: Array<TabItemProps> = useMemo(
    () => [
      { label: t('process') },
      {
        label: t('associated_automations'),
        badgeNumber: process?.automationsCount,
      },
    ],
    [process],
  );

  const tabList = useMemo(() => {
    if (!parsedProcessId || !process) {
      return [];
    }

    return [
      {
        tab: (
          <TablePaginationContextProvider>
            <WorkflowsTable
              initialWorkflows={sortedWorkflows}
              isLoadingWorkflows={isLoadingProcess}
              businessAreaId={process.businessAreaId}
            />
            <Button
              className="h-6 w-full justify-between border-blueNuit p-2 font-black text-blueNuit"
              variant="text"
              onClick={handleModalState}
            >
              <span className="flex flex-row">+ {t('add_workflow')}</span>
            </Button>
          </TablePaginationContextProvider>
        ),
      },
      {
        tab: (
          <TablePaginationContextProvider>
            <AssociatedAutomationsTab processId={parsedProcessId} />
          </TablePaginationContextProvider>
        ),
      },
    ];
  }, [
    handleModalState,
    isLoadingProcess,
    parsedProcessId,
    process,
    sortedWorkflows,
  ]);

  return (
    <CustomPageContainer>
      <PageDetailsActions onBack={routeChange} breadcrumbs={breadcrumbs}>
        <LabelIcon
          label={t('process')}
          icon={<ProcessesIcon className="size-5 text-blueNuit" />}
        />
      </PageDetailsActions>
      <ProcessHeaderRow
        process={process}
        handleStatus={handleStatus}
        status={status}
        isLoading={isLoadingProcess}
      />
      <div className="flex flex-wrap gap-2">
        <ProcessTimeDistributionCard processId={parsedProcessId} />
        <ProcesssLastRunSuccessRateCard processId={parsedProcessId} />
        <ProcessOverallSuccessRateCard processId={parsedProcessId} />
      </div>
      <TablePaginationContextProvider>
        <ProcessPerformanceCards processId={parsedProcessId} />
      </TablePaginationContextProvider>
      <TabsWrapper tabs={tabs} tabList={tabList} />
      <ProcessWorkflowModal
        handleClose={handleModalState}
        isOpen={isOpen}
        process={process}
        disableFields={[
          'processId',
          'organizationId',
          'companyId',
          'businessAreaId',
        ]}
      />
    </CustomPageContainer>
  );
};
